import React, { useState, useEffect } from "react";
import { Button, CircularProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SearchIcon from "@mui/icons-material/Search";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { showErrorToast, showSuccessToast } from "../../../common/components/Helper/Utils";
import { createlabelSetting, savelabelSetting, updatelabelSetting } from "./actions/administration";
import { SpinnerRoundFilled } from "spinners-react";

const useStyles = makeStyles(() => ({
  root: {
    fontFamily: "'Inter', sans-serif",
    minHeight: "100vh",
    padding: "32px",
  },
  pageHeader: {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: "28px",
  },
  headerTitle: {
    fontSize: "22px",
    fontWeight: 600,
    color: "#0a0a0a",
    lineHeight: 1.2,
    letterSpacing: "0.00735em",
  },
  headerSubtitle: {
    fontSize: "13px",
    color: "#6b7280",
    marginTop: "5px",
  },
  sectionBlock: {
    marginBottom: "24px",
  },
  sectionLabelRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "8px",
    paddingLeft: "2px",
  },
  sectionLine: {
    width: "14px",
    height: "2px",
    backgroundColor: "#0a0a0a",
    borderRadius: "2px",
    flexShrink: 0,
  },
  sectionTitle: {
    fontSize: "1rem",
    fontWeight: 600,
    color: "#0a0a0a",
    textTransform: "uppercase",
    letterSpacing: "0.0075em",
  },
  groupBox: {
    backgroundColor: "#fff",
    borderRadius: "10px",
    border: "1px solid #e5e7eb",
    overflow: "hidden",
    maxWidth: "760px",
  },
  labelRow: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    padding: "13px 18px",
    borderBottom: "1px solid #f3f4f6",
    "&:last-child": { borderBottom: "none" },
    "&:hover": { backgroundColor: "#fafafa" },
  },
  defaultSide: {
    flex: "0 0 220px",
    minWidth: 0,
  },
  defaultLabelText: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#111827",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  defaultSubText: {
    fontSize: "11px",
    color: "#9ca3af",
    marginTop: "2px",
    fontWeight: 400,
  },
  arrowIcon: {
    color: "#d1d5db",
    fontSize: "15px !important",
    flexShrink: 0,
  },
  customSide: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
  },
  customText: {
    fontSize: "13px",
    fontWeight: 500,
    color: "#374151",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  customTextModified: {
    color: "#15803d",
    fontWeight: 600,
  },
  customInput: {
    width: "300px",
    padding: "7px 11px",
    border: "1px solid #d1d5db",
    borderRadius: "7px",
    fontSize: "13px",
    color: "#111827",
    outline: "none",
    backgroundColor: "transparent",
    boxSizing: "border-box",
    fontFamily: "'Inter', sans-serif",
    transition: "border-color 0.15s, box-shadow 0.15s",
    "&:focus": {
      borderColor: "#6b7280",
      boxShadow: "0 0 0 3px rgba(10,10,10,0.06)",
    },
  },
  actionBar: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: "10px",
    marginTop: "24px",
    paddingTop: "20px",
    maxWidth: "760px",
  },
  actionBtn: {
    width: "80px !important",
    minWidth: "80px !important",
    fontWeight: "600 !important",
    fontSize: "13px !important",
    padding: "8px 0 !important",
    borderRadius: "8px !important",
    textTransform: "none !important",
  },
  editBtn: {
    marginLeft: "280px !important",
  },
  emptyState: {
    textAlign: "center",
    padding: "56px 20px",
  },
  emptyTitle: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#374151",
    marginBottom: "4px",
  },
  emptyText: {
    fontSize: "13px",
    color: "#9ca3af",
  },
}));

const DEFAULT_LABELS = [
  { id: 1, defaultLabel: "Target Wholesale Price" },
  { id: 2, defaultLabel: "Target End Customer Price" },
  { id: 3, defaultLabel: "Target End Customer Wire Price" },
  { id: 4, defaultLabel: "Tag Price" },
  { id: 5, defaultLabel: "Used/Good" },
  { id: 6, defaultLabel: "Mint/Very Good" },
  { id: 7, defaultLabel: "Retail Ready/Like New" },
  { id: 8, defaultLabel: "New" },
  { id: 9, defaultLabel: "Used" },
];

const FIELD_MAP = [
  {
    id: 1,
    defaultKey: "default_wholesale_price",
    customKey: "custom_wholesale_price",
  },
  {
    id: 2,
    defaultKey: "default_end_customer_price",
    customKey: "custom_end_customer_price",
  },
  {
    id: 3,
    defaultKey: "default_end_customer_wire_price",
    customKey: "custom_end_customer_wire_price",
  },
  {
    id: 4,
    defaultKey: "default_tag_price",
    customKey: "custom_tag_price",
  },
  {
    id: 5,
    defaultKey: "default_used_good",
    customKey: "custom_used_good",
  },
  {
    id: 6,
    defaultKey: "default_mint_verygood",
    customKey: "custom_mint_verygood",
  },
  {
    id: 7,
    defaultKey: "default_retail_likenew",
    customKey: "custom_retail_likenew",
  },
  {
    id: 8,
    defaultKey: "default_new",
    customKey: "custom_new",
  },
  {
    id: 9,
    defaultKey: "default_used",
    customKey: "custom_used",
  }
];

const mapResponseToLabels = (apiData) => {
  return FIELD_MAP.map(({ id, defaultKey, customKey }) => {
    const fallback =
      DEFAULT_LABELS.find((x) => x.id === id)?.defaultLabel || "";

    return {
      id,
      defaultLabel: apiData
        ? apiData[defaultKey] || fallback
        : fallback,

      customLabel: apiData
        ? apiData[customKey] || apiData[defaultKey] || fallback
        : fallback,
    };
  });
};

const mapLabelsToPayload = (
  labelsArr,
  accountid,
  userId,
  id
) => {
  const payload = {
    userId,
    accountid,
  };

  if (id) {
    payload.id = id;
  }

  FIELD_MAP.forEach(({ id: fieldId, defaultKey, customKey }) => {
    const row = labelsArr.find((x) => x.id === fieldId);

    if (row) {
      payload[defaultKey] = row.defaultLabel;
      payload[customKey] = row.customLabel;
    }
  });

  payload.default_condition = "Condition";
  payload.custom_condition = "Condition";

  return payload;
};

const CustomLabel = () => {
  const classes = useStyles();
  const accountid = window.sessionStorage.getItem("accountId");
  const userId = JSON.parse(sessionStorage.getItem("userDetails")).userId;
  const initial = mapResponseToLabels(null);
  const [labels, setLabels] = useState(initial);
  const [savedLabels, setSavedLabels] = useState(initial);
  const [snapshot, setSnapshot] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isFirstSave, setIsFirstSave] = useState(true);
  const [labelId, setLabelId] = useState(null);
  const [loader, setloader] = useState(true);
  const [showMaxLengthError, setShowMaxLengthError] = useState(false);
  const [isFieldTouched, setIsFieldTouched] = useState(false);

  useEffect(() => {
    setloader(true);
    const storedData = sessionStorage.getItem("customLabelSettings");

    if (storedData) {
      const parsedData = JSON.parse(storedData);

      setLabelId(parsedData.id);
      setIsFirstSave(false);

      const mapped = mapResponseToLabels(parsedData);

      setLabels(mapped);
      setSavedLabels(mapped);
    }

    createlabelSetting((err, res) => {
      if (err) {
        setloader(false);
        return;
      }
      if (res && res.data) {
        setLabelId(res.data.id);
        setIsFirstSave(false);

        const mapped = mapResponseToLabels(res.data);

        setLabels(mapped);
        setSavedLabels(mapped);

        sessionStorage.setItem(
          "customLabelSettings",
          JSON.stringify(res.data)
        );
      }
       setloader(false);
    });
  }, []);

  const handleEdit = () => {
    setSnapshot(labels.map((l) => ({ ...l })));
    setEditMode(true);
    setIsFieldTouched(false);
  };

  const handleCancel = () => {
    setLabels(snapshot);
    setSnapshot(null);
    setEditMode(false);
    setIsFieldTouched(false);
  };

 const handleCustomChange = (id, value) => {
    const sanitizedValue = value.slice(0, 30);

    setLabels((prev) =>
      prev.map((l) =>
        l.id === id
          ? { ...l, customLabel: sanitizedValue }
          : l
      )
    );
  };

  const hasChanges =
    JSON.stringify(
      labels.map(({ id, customLabel }) => ({ id, customLabel }))
    ) !==
    JSON.stringify(
      savedLabels.map(({ id, customLabel }) => ({ id, customLabel }))
  );

 const handleSave = () => {
  setShowMaxLengthError(false);
  setloader(true);
  setSaving(true);

  const payload = mapLabelsToPayload(
    labels,
    accountid,
    userId,
    labelId
  );

  const onComplete = (err) => {
  setloader(false);
  setSaving(false);

  if (err) {
    showErrorToast("Save Failed");
    return;
  }
  sessionStorage.setItem(
    "customLabelSettings",
    JSON.stringify(payload)
  );

  createlabelSetting((e, res) => {
    if (e) return;

    if (res && res.data) {
      setLabelId(res.data.id);
      setIsFirstSave(false);

      const mapped = mapResponseToLabels(res.data);

      setLabels(mapped);
      setSavedLabels(mapped);

      sessionStorage.setItem(
        "customLabelSettings",
        JSON.stringify(res.data)
      );
    }

    setEditMode(false);
    setIsFieldTouched(false);
    showSuccessToast("Custom Label updated successfully");
  });
};

  if (labelId) {

    updatelabelSetting(payload, onComplete);

  } else {

    savelabelSetting(payload, onComplete);

  }

};

  const filtered = labels.filter(
    (l) =>
      (l.defaultLabel || "").toLowerCase().includes(search.toLowerCase()) ||
      (l.customLabel || "").toLowerCase().includes(search.toLowerCase())
  );

  const priceTargetRows = filtered.filter((row) => row.id <= 4);
  const conditionRows = filtered.filter((row) => row.id >= 5);

  const isModified = (row) => {
    const saved = savedLabels.find((s) => s.id === row.id);
    return saved && saved.customLabel !== row.defaultLabel;
  };

  const renderRow = (row) => (
    <div key={row.id} className={classes.labelRow}>
      <div className={classes.defaultSide}>
        <div className={classes.defaultLabelText}>{row.defaultLabel}</div>
        <div className={classes.defaultSubText}>System default</div>
      </div>

      <ArrowForwardIcon className={classes.arrowIcon} />

      <div className={classes.customSide}>
        {editMode ? (
          <input
            className={classes.customInput}
            value={row.customLabel}
            placeholder={row.defaultLabel}
          onFocus={() => {
              setIsFieldTouched(true);
              if (row.customLabel === row.defaultLabel) {
                handleCustomChange(row.id, "");
              }
            }}
            onBlur={(e) => {
              if (e.target.value.trim() === "") {
                setLabels((prev) =>
                  prev.map((l) =>
                    l.id === row.id ? { ...l, customLabel: l.defaultLabel } : l
                  )
                );
              }
            }}
            onChange={(e) => {
              const value = e.target.value;

              if (value.length > 30) {
                setShowMaxLengthError(true);
                return;
              }

              setShowMaxLengthError(false);
              handleCustomChange(row.id, value);
            }}
          />
        ) : (
          <span
            className={`${classes.customText} ${
              isModified(row) ? classes.customTextModified : ""
            }`}
          >
            {row.customLabel}
          </span>
        )}
      </div>
    </div>
  );

  const renderSection = (title, rows) => {
    if (rows.length === 0) return null;
    return (
      <div className={classes.sectionBlock}>
        <div className={classes.sectionLabelRow}>
          <span className={classes.sectionTitle}>{title}</span>
        </div>
        <div className={classes.groupBox}>
          {rows.map((row) => renderRow(row))}
        </div>
      </div>
    );
  };

   if (loader) {
      return (
        <div className="d-spinner">
          <SpinnerRoundFilled
            size={51}
            thickness={125}
            speed={80}
            color="rgba(57, 107, 172, 1)"
            className="spp"
          />
        </div>
      );
    }

  return (
    <div className={classes.root}>
      <div className={classes.pageHeader}>
        <div>
          <div className={classes.headerTitle}>Custom Labels</div>
          <div className={classes.headerSubtitle}>
            Customize the names of fields displayed throughout your organization.
          </div>
        </div>
        {!editMode && (
          <Button
            className={`primary-bg color-white text-center1 ${classes.editBtn}`}
            onClick={handleEdit}
          >
            Edit
          </Button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className={classes.emptyState}>
          <SearchIcon
            style={{
              fontSize: "32px",
              color: "#d1d5db",
              display: "block",
              margin: "0 auto 10px",
            }}
          />
          <div className={classes.emptyTitle}>No labels found</div>
          <div className={classes.emptyText}>Try a different search term.</div>
        </div>
      ) : (
        <>
          {renderSection("Price Targets", priceTargetRows)}
          {renderSection("Condition", conditionRows)}
        </>
      )}
      {showMaxLengthError && (
        <div
          style={{
            color: "red",
            fontSize: "12px",
            marginTop: "10px",
            marginBottom: "10px",
          }}
        >
          Maximum 30 characters allowed.
        </div>
      )}

      {editMode && (
        <div className={classes.actionBar}>
          <Button
            className={`outlined-btn color-111 text-center1 ${classes.actionBtn}`}
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            className={`primary-bg color-white text-center1 ${classes.actionBtn}`}
            onClick={handleSave}
            disabled={saving || !hasChanges}
            startIcon={
              saving ? (
                <CircularProgress size={13} style={{ color: "#fff" }} />
              ) : null
            }
          >
            {saving ? "Saving" : "Save"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default CustomLabel;